import { useState, useCallback, useEffect, useRef, useMemo } from 'react';

const STORAGE_KEY = 'oil-table-column-settings';
const STATIC_KEYS = ['brand', 'type', 'model', 'tara'];

function load(): { staticOrder: string[]; supplierOrder: number[] } | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    if (Array.isArray(p.staticOrder) && Array.isArray(p.supplierOrder)) return p;
  } catch {}
  return null;
}

function save(staticOrder: string[], supplierOrder: number[]) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ staticOrder, supplierOrder })); } catch {}
}

export function useColumnSettings(supplierIds: number[]) {
  const [staticOrder, setStaticOrder] = useState<string[]>(() => {
    const s = load();
    if (s) {
      const valid = s.staticOrder.filter(k => STATIC_KEYS.includes(k));
      for (const k of STATIC_KEYS) if (!valid.includes(k)) valid.push(k);
      return valid;
    }
    return [...STATIC_KEYS];
  });

  const [supplierOrder, setSupplierOrder] = useState<number[]>(() => {
    const s = load();
    if (s?.supplierOrder) {
      const set = new Set(supplierIds);
      const valid = s.supplierOrder.filter((id: number) => set.has(id));
      const existing = new Set(valid);
      return [...valid, ...supplierIds.filter(id => !existing.has(id))];
    }
    return [...supplierIds];
  });

  // Sync when supplier list changes
  const prevRef = useRef(supplierIds.join(','));
  useEffect(() => {
    const key = supplierIds.join(',');
    if (key === prevRef.current) return;
    prevRef.current = key;
    setSupplierOrder(prev => {
      const set = new Set(supplierIds);
      const valid = prev.filter(id => set.has(id));
      const existing = new Set(valid);
      return [...valid, ...supplierIds.filter(id => !existing.has(id))];
    });
  }, [supplierIds]);

  // Persist
  useEffect(() => { save(staticOrder, supplierOrder); }, [staticOrder, supplierOrder]);

  const columnOrder = useMemo(
    () => [...staticOrder, ...supplierOrder.map(id => `supplier:${id}`)],
    [staticOrder, supplierOrder],
  );

  const reorderStatic = useCallback((fromKey: string, toKey: string) => {
    setStaticOrder(prev => {
      const a = [...prev];
      const fi = a.indexOf(fromKey), ti = a.indexOf(toKey);
      if (fi < 0 || ti < 0 || fi === ti) return prev;
      a.splice(fi, 1);
      a.splice(ti, 0, fromKey);
      return a;
    });
  }, []);

  const reorderSupplier = useCallback((fromKey: string, toKey: string) => {
    const fid = parseInt(fromKey.split(':')[1]);
    const tid = parseInt(toKey.split(':')[1]);
    setSupplierOrder(prev => {
      const a = [...prev];
      const fi = a.indexOf(fid), ti = a.indexOf(tid);
      if (fi < 0 || ti < 0 || fi === ti) return prev;
      a.splice(fi, 1);
      a.splice(ti, 0, fid);
      return a;
    });
  }, []);

  return { columnOrder, staticOrder, supplierOrder, reorderStatic, reorderSupplier };
}
